import AutocompleteCombobox from '../combobox/autocomplete-combobox';
import DropdownCombobox from '../combobox/dropdown-combobox';
import type { Size } from './Tyreselector';

type State = {
  width: number | null;
  height: number | null;
  rim: number | null;
};

export default class TyreSelectorBySize {
  dom: {
    root: HTMLElement, // This module's root element
    form: HTMLFormElement,
    filters: HTMLFormElement,
    width: HTMLSelectElement,
    height: HTMLSelectElement,
    rim: HTMLSelectElement,
  };

  // Parent tyre selector instance
  parent;

  // Tyre selector filters state
  #state: State = {
    width: null,
    height: null,
    rim: null,
  };

  #storageKey = 'ts__size';
  #updatingFilters = false;
  #submitting = false;

  /**
   * Tyre selector by size module
   * @param {HTMLElement} element - Root element of the size module of tyre selector
   * @param {TyreSelector} parent - Parent tyre selector instance
   */
  constructor(element: HTMLElement, parent) {
    this.parent = parent;
    this.dom = {} as any;
    this.dom.root = element;
    this.dom.form = element.querySelector('form[name="sizeSearchForm"]') as HTMLFormElement;

    if(this.dom.form) {
      Object.keys(this.#state).forEach(key => {
        this.dom[key] = this.dom.form.querySelector(`select[name="${key}"]`) as HTMLSelectElement;
      });
    }

    if(this.parent.sizeData.length && Object.keys(this.dom).every(key => this.dom[key])) {
      // Necessary data and dom elements found
      console.log('[TyreSelectorBySize]: init size module');
      this.#initState();
      this.#initDropdowns();
      this.#attachEventListeners();
    } else {
      console.error('[TyreSelectorBySize]: missing data or dom elements');
    }
  }

  /**
   * Initialises state from dom select values
   */
  #initState() {
    // Default state
    const initialState = this.getState();

    // Check values from select elements (values set by server)
    Object.keys(this.#state).forEach(key => {
      if(this.dom[key]) {
        const value = this.getSelectValue(this.dom[key]);
        initialState[key] = value;
      }
    });

    // Check storage for user's previous values
    const storageValues = sessionStorage.getItem(this.#storageKey);
    if(storageValues) {
      try {
        const storageState = JSON.parse(storageValues);
        Object.keys(storageState).forEach(key => {
          if(this.dom[key]) {
            initialState[key] = storageState[key];
          }
        });
      } catch(e) {
        console.error('[TyreSelectorBySize]: Failed to parse storage values', e);
      }
    }

    this.update(initialState);
  }

  /**
   * Initialises custom dropdowns for native selects
   */
  #initDropdowns() {
    Object.keys(this.#state)
      .forEach(key => {
        const selectEl = this.dom[key];

        new DropdownCombobox({
          select: selectEl,
          cssClassPrefix: 'combobox',
        });
      });
  }

  /**
   * Attach event listeners to relevant dom elements
   */
  #attachEventListeners() {
    this.dom.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.dom.form.addEventListener('reset', this.onFormReset.bind(this));

    Object.keys(this.#state).forEach(key => {
      this.dom[key].addEventListener('change', this.onSelectChange.bind(this));
    });
  }

  setSessionData(data) {
    sessionStorage.setItem(this.#storageKey, JSON.stringify(data));
  }

  /**
   * Returns current filter values
   */
  getState() {
    return {...this.#state};
  }

  onFormSubmit(event: Event) {
    event.preventDefault();

    if(this.#submitting) {
      return;
    }

    this.#submitting = true;
    this.parent.setLoading(true);

    // Store form state to session storage
    const newState = this.getState();
    sessionStorage.setItem(this.#storageKey, JSON.stringify(newState));

    // Store tyre data
    this.parent.storeTyreData({
      width: newState.width,
      height: newState.height,
      rim: newState.rim,
    }, 'size');

    if(['embed', 'default'].includes(this.parent.type)) {
      // Redirect to size page
      this.parent.setVisibility('hidden');
      const { width, height, rim } = newState;

      const analyticsEvent = {
        event: 'tyre_search_submit',
        tyre_search_type: 'Size Search',
        tyre_size: `${width}/${height}R${rim}`,
      }

      this.parent.setView( 'size');

      this.parent.redirectToSizePage(width, height, rim, { analyticsEvent });
    } else {
      // TODO: Do something else
      this.parent.setLoading(false);
    }
  }

  /**
   * Clear form values and session storage
   */
  onFormReset(event: Event) {
    const values = this.getState();

    for (let key in values) {
      values[key] = null;
    }

    sessionStorage.removeItem(this.#storageKey);
    this.parent.setView(null);
    this.parent.setVisibility('visible');

    this.update(values);

    // Clear tyreData
    this.parent.storeTyreData({}, 'size');
  }


  /**
   * Handles native select element change event
   */
  onSelectChange(event: Event) {
    // Callback was caused by internal change in filters
    if (this.#updatingFilters) {
      return;
    }

    const select = event.target as HTMLSelectElement;
    const selectName = select.name;
    const selectValue = select.value === '' ? null : Number(select.value);

    const newState = this.getState();
    let resetFields = false;
    for (let key in newState) {
      if (selectName === key) {
        newState[selectName] = selectValue;
        resetFields = true;
      } else if (resetFields) {
        newState[key] = null;
      }
    }

    this.update(newState);
  }

  /**
   * Formats select value to number or null
   * All values are strings in select elements, make empty string null and others numeric.
   */
  getSelectValue(select: HTMLSelectElement) {
    return select.value === '' ? null : Number(select.value);
  }

  /**
   * Updates internal state and populates select elements with available options.
   */
  update(newState: State) {
    const stateKeys = Object.keys(this.#state);
    const oldState = this.getState();
    const futureState = { ...oldState, ...newState };
    const changed: HTMLSelectElement[] = [];

    const updateSelectOptions = (element: HTMLSelectElement, availableOptions) => {
      if (oldState[element.name] !== futureState[element.name]) {
        changed.push(element);
      }

      // Remove all options except the empty value one
      Array.from(element.options).filter((opt) => opt.value !== '').forEach((opt) => {
        opt.remove();
      });

      // Populate select with available options
      availableOptions.forEach((optionValue) => {
        const option = new Option(String(optionValue), String(optionValue), false, optionValue === futureState[element.name]);
        element.add(option);
      });
    };

    // Fields are sequential, change one field, next fields are updated
    stateKeys.forEach((key, keyIndex) => {
      const selectElement = this.dom[key] as HTMLSelectElement;
      const previousFieldValue = futureState[stateKeys[keyIndex - 1]];
      const enableField = typeof previousFieldValue === 'undefined' || previousFieldValue !== null;

      if(enableField) {
        const fieldValues = {...futureState};
        const nextFieldKeys = stateKeys.slice(keyIndex); // self and next
        nextFieldKeys.forEach((fieldKey) => {
          fieldValues[fieldKey] = null;
        });
        const matchingSizes = this.filterSizes(fieldValues);
        let availableOptions = matchingSizes.map(size => size[keyIndex]);
        // Filter out duplicates
        availableOptions = [...new Set(availableOptions)];
        availableOptions.sort((a: any, b: any) => a - b);

        updateSelectOptions(selectElement, availableOptions);
        selectElement.disabled = false;
      } else {
        updateSelectOptions(selectElement, []);
        selectElement.value = '';
        selectElement.disabled = true;
      }
    });

    // Update internal state
    this.#state = futureState;

    // Trigger native event on all elements that were changed
    // Combobox needs this, if it's used
    this.#updatingFilters = true;
    changed.forEach((el) => {
      el.dispatchEvent(new Event('change', { bubbles: true }));
    });
    this.#updatingFilters = false;

    // Enable submit button if all fields have a value
    const allFieldsHaveValue = Object.keys(this.#state).every(key => this.#state[key] !== null);
    const submitBtn = this.dom.form.querySelector('button[type="submit"]') as HTMLButtonElement;
    if(submitBtn) {
      submitBtn.disabled = !allFieldsHaveValue;
    }
  }

  /**
   * Filter all available sizes based on given values.
   * All values must match.
   */
  filterSizes(values: State): Size[] {
    const matchingSizes = this.parent.sizeData.filter(size => {
      return Object.keys(values).every((key, keyIndex) => {
        const stateValue = values[key];
        const sizeValue = size[keyIndex];

        if(stateValue && sizeValue !== stateValue) {
          return false;
        }

        return true;
      });
    });

    return matchingSizes;
  }

}
